<template lang="pug">

    div

        h3.ui.header Lieferschein
        p Lieferschein für dieses Produkt erstellen.
        .ui.error.message(v-if="errors.deliveryNote")
            i.warning.circle.icon
            | {{ errors.deliveryNote }}
        .ui.info.message(v-if="existingLink('deliveryNote') && !results.deliveryNote")
            | Bereits erstelltes Dokument –
            |
            a(:href="existingLink('deliveryNote')" target="_blank")
                i.download.icon
                | Lieferschein herunterladen
        .ui.info.message(v-if="results.deliveryNote")
            | PDF erfolgreich erstellt –
            |
            a(:href="results.deliveryNote.downloadUrl" target="_blank")
                i.download.icon
                | Lieferschein herunterladen
        button.ui.primary.button(
            @click="onGenerateDeliveryNote"
            :class="{ loading: loading.deliveryNote, disabled: loading.deliveryNote }"
        )
            i.file.pdf.outline.icon
            | {{ existingLink('deliveryNote') ? 'Neu erstellen' : 'Lieferschein erstellen' }}


        h3.ui.header Angebote &amp; Auftragsbestätigungen
        p.ui.grey.text(v-if="!jobs.length") Keine Lieferantenanfragen für dieses Produkt vorhanden.
        table.ui.celled.table(v-if="jobs.length")
            thead
                tr
                    th Lieferant
                    th Status
                    th Angebot PDF
                    th Auftragsbestätigung PDF
            tbody
                tr(v-for="job in jobs" :key="job.id")
                    td {{ supplierName(job) }}
                    td {{ job.status }}
                    td
                        button.ui.mini.primary.button(
                            @click="onGenerateOffer(job.id)"
                            :class="{ loading: isLoading('offer', job.id), disabled: isLoading('offer', job.id) }"
                        )
                            i.file.pdf.outline.icon
                            | {{ existingLink(`offer_${job.id}`) ? 'Neu erstellen' : 'PDF erstellen' }}
                        div(v-if="existingLink(`offer_${job.id}`) && !getResult('offer', job.id)")
                            a.ui.mini.blue.basic.button(:href="existingLink(`offer_${job.id}`)" target="_blank" style="margin-top: 5px")
                                i.download.icon
                                | Download
                        div(v-if="getResult('offer', job.id)")
                            a.ui.mini.blue.basic.button(:href="getResult('offer', job.id).downloadUrl" target="_blank" style="margin-top: 5px")
                                i.download.icon
                                | Download
                    td
                        button.ui.mini.primary.button(
                            @click="onGenerateOrderConfirmation(job.id)"
                            :class="{ loading: isLoading('orderConfirmation', job.id), disabled: isLoading('orderConfirmation', job.id) }"
                        )
                            i.file.pdf.outline.icon
                            | {{ existingLink(`orderConfirmation_${job.id}`) ? 'Neu erstellen' : 'PDF erstellen' }}
                        div(v-if="existingLink(`orderConfirmation_${job.id}`) && !getResult('orderConfirmation', job.id)")
                            a.ui.mini.blue.basic.button(:href="existingLink(`orderConfirmation_${job.id}`)" target="_blank" style="margin-top: 5px")
                                i.download.icon
                                | Download
                        div(v-if="getResult('orderConfirmation', job.id)")
                            a.ui.mini.blue.basic.button(:href="getResult('orderConfirmation', job.id).downloadUrl" target="_blank" style="margin-top: 5px")
                                i.download.icon
                                | Download

</template>

<script>
    import * as R from 'ramda'
    import store from '@/store'

    export default {

        async beforeRouteEnter(to, from, next) {
            const productId = R.prop('id', to.params)

            await Promise.all([
                store.dispatch('products/loadOne', { id: productId }),
                store.dispatch('jobs/loadAllByProductId', { productId }),
            ])

            // Lieferanten-Unternehmen laden
            const jobs = store.getters['jobs/allByProductId'](Number(productId))
            const supplierIds = [...new Set(jobs.map(j => j.supplier || j.supplierId).filter(Boolean))]
            await Promise.all(supplierIds.map(id => store.dispatch('companies/loadOne', { id })))

            next()
        },

        data() {
            return {
                loading: {},
                results: {},
                errors: {},
                existingLinks: {},
            }
        },

        async created() {
            await this.loadExistingLinks()
        },

        computed: {

            productId() {
                return Number(R.prop('id', this.$route.params))
            },

            variantId() {
                return this.$route.params.variantId ? Number(this.$route.params.variantId) : null
            },

            jobs() {
                return store.getters['jobs/allByProductId'](this.productId)
            },

        },

        methods: {

            async loadExistingLinks() {
                const checks = []

                // Lieferschein-Link laden
                checks.push(
                    this.$store.dispatch('pdfs/findLinkByLabel', { label: `pdf:delivery-note:${this.productId}` })
                        .then(link => { if (link) this.$set(this.existingLinks, 'deliveryNote', link.downloadUrl) })
                        .catch(() => {})
                )

                // Job-Links laden
                for (const job of this.jobs) {
                    const jobId = job.id
                    checks.push(
                        this.$store.dispatch('pdfs/findLinkByLabel', { label: `pdf:offer:${jobId}` })
                            .then(link => { if (link) this.$set(this.existingLinks, `offer_${jobId}`, link.downloadUrl) })
                            .catch(() => {})
                    )
                    checks.push(
                        this.$store.dispatch('pdfs/findLinkByLabel', { label: `pdf:order-confirmation:${jobId}` })
                            .then(link => { if (link) this.$set(this.existingLinks, `orderConfirmation_${jobId}`, link.downloadUrl) })
                            .catch(() => {})
                    )
                }

                await Promise.all(checks)
            },

            existingLink(key) {
                return this.existingLinks[key] || null
            },

            supplierName(job) {
                const supplierId = job.supplier || job.supplierId
                const company = supplierId ? store.getters['companies/byId'](supplierId) : null
                return company ? company.name : `Lieferant #${supplierId || job.id}`
            },

            isLoading(type, jobId) {
                return !!this.loading[`${type}_${jobId}`]
            },

            getResult(type, jobId) {
                return this.results[`${type}_${jobId}`] || null
            },

            async onGenerateDeliveryNote() {
                this.$set(this.loading, 'deliveryNote', true)
                this.$set(this.errors, 'deliveryNote', null)
                try {
                    const result = await this.$store.dispatch('pdfs/generateDeliveryNote', { productId: this.productId })
                    this.$set(this.results, 'deliveryNote', result)
                } catch (e) {
                    this.$set(this.errors, 'deliveryNote', 'Fehler beim Erstellen des Lieferscheins.')
                } finally {
                    this.$set(this.loading, 'deliveryNote', false)
                }
            },

            async onGenerateOffer(jobId) {
                const key = `offer_${jobId}`
                this.$set(this.loading, key, true)
                try {
                    const result = await this.$store.dispatch('pdfs/generateOffer', { jobId })
                    this.$set(this.results, key, result)
                } catch (e) {
                    this.$set(this.errors, key, 'Fehler beim Erstellen des Angebots.')
                } finally {
                    this.$set(this.loading, key, false)
                }
            },

            async onGenerateOrderConfirmation(jobId) {
                const key = `orderConfirmation_${jobId}`
                this.$set(this.loading, key, true)
                try {
                    const result = await this.$store.dispatch('pdfs/generateOrderConfirmation', { jobId, variantId: this.variantId })
                    this.$set(this.results, key, result)
                } catch (e) {
                    this.$set(this.errors, key, 'Fehler beim Erstellen der Auftragsbestätigung.')
                } finally {
                    this.$set(this.loading, key, false)
                }
            },

        },

    }

</script>
